// The option controls as a plain object, persisted to localStorage. Only
// settings live here; the loaded capture never does.

import {
  FILTER_ORDERS,
  MAX_REJECTION_DB,
  MIN_REJECTION_DB,
  type FilterSettings,
} from './overlays/filter.ts'
import type { Threshold } from './overlays/fit.ts'
import { LORA_BANDWIDTHS_HZ, LORA_CODING_RATES, LORA_SPREADING_FACTORS, type SignalSettings } from './overlays/signal.ts'
import type { Options } from './render/layout.ts'
import { DEFAULT_PALETTE, isPaletteName, type PaletteName } from './render/palette.ts'
import { browserTimeZone, isValidTimeZone } from './tz.ts'
import type { TimeInput } from './units.ts'

export type TimeDisplay = 'utc' | 'local'

export interface Settings extends Options, SignalSettings, FilterSettings {
  palette: PaletteName
  timeDisplay: TimeDisplay
  /** null = use the browser's zone for rtl_power's naive timestamps. */
  captureZoneOverride: string | null
  exportWidth: number
  /** Scheme ids drawn as overlays, in panel order. */
  visibleSchemes: string[]
  /** Ids of the options sections the reader has collapsed; all open by default. */
  collapsedSections: string[]
  /** Whether the signal check is on: the chart, the pointer's column and the pin. */
  signalCheckOn: boolean
  /** Occupancy threshold: above the per-bin floor by a margin, or above an absolute level. */
  thresholdMode: 'relative' | 'absolute'
  thresholdMarginDb: number
  thresholdAbsDb: number
  /**
   * How much of our own transmission the search will let the filter eat. The
   * default is 3 dB, which is the filter's own passband: the bandwidth control
   * is defined by it, so "stay inside the filter" needs no other number.
   */
  fitLossBudgetDb: number
}

const KEY = 'spectrum-mapper.settings.v1'

export function defaultSettings(): Settings {
  return {
    palette: DEFAULT_PALETTE,
    dbMin: null,
    dbMax: null,
    lowHz: null,
    highHz: null,
    begin: null,
    end: null,
    headSec: null,
    tailSec: null,
    compress: 0,
    offsetHz: 0,
    ytickSec: null,
    timeDisplay: 'utc',
    captureZoneOverride: null,
    captureZone: browserTimeZone(),
    exportWidth: 1024,
    visibleSchemes: [],
    collapsedSections: [],
    signalBwHz: 250e3,
    signalSf: 11,
    signalCr: 5,
    payloadBytes: 64,
    signalCheckOn: false,
    filterOn: false,
    filterCenterHz: null,
    filterBwHz: null,
    filterOrder: 3,
    filterRejectionDb: 40,
    thresholdMode: 'relative',
    thresholdMarginDb: 6,
    thresholdAbsDb: 0,
    fitLossBudgetDb: 3,
  }
}

/** The search threshold as fit.ts takes it. */
export function threshold(settings: Settings): Threshold {
  return settings.thresholdMode === 'absolute'
    ? { kind: 'absolute', db: settings.thresholdAbsDb }
    : { kind: 'relative', marginDb: settings.thresholdMarginDb }
}

const positive = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback

const finiteOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

const positiveOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null

export function displayZone(settings: Settings): string {
  return settings.timeDisplay === 'utc' ? 'UTC' : browserTimeZone()
}

export function effectiveCaptureZone(settings: Settings): string {
  const zone = settings.captureZoneOverride
  return zone && isValidTimeZone(zone) ? zone : browserTimeZone()
}

export function loadSettings(): Settings {
  const defaults = defaultSettings()
  let raw: string | null = null
  try {
    raw = localStorage.getItem(KEY)
  } catch {
    return defaults
  }
  if (!raw) return defaults
  try {
    const parsed = JSON.parse(raw) as Partial<Settings>
    const merged: Settings = { ...defaults, ...parsed }
    if (!isPaletteName(merged.palette)) merged.palette = defaults.palette
    if (merged.timeDisplay !== 'utc' && merged.timeDisplay !== 'local') merged.timeDisplay = 'utc'
    merged.begin = validTime(merged.begin)
    merged.end = validTime(merged.end)
    merged.captureZone = effectiveCaptureZone(merged)
    merged.visibleSchemes = Array.isArray(merged.visibleSchemes)
      ? merged.visibleSchemes.filter((id): id is string => typeof id === 'string')
      : []
    merged.collapsedSections = Array.isArray(merged.collapsedSections)
      ? merged.collapsedSections.filter((id): id is string => typeof id === 'string')
      : []
    if (!LORA_BANDWIDTHS_HZ.includes(merged.signalBwHz)) merged.signalBwHz = defaults.signalBwHz
    if (!LORA_SPREADING_FACTORS.includes(merged.signalSf)) merged.signalSf = defaults.signalSf
    if (!LORA_CODING_RATES.includes(merged.signalCr)) merged.signalCr = defaults.signalCr
    merged.payloadBytes = Math.min(255, Math.round(positive(merged.payloadBytes, defaults.payloadBytes)))
    merged.signalCheckOn = merged.signalCheckOn === true
    merged.filterOn = merged.filterOn === true
    merged.filterCenterHz = finiteOrNull(merged.filterCenterHz)
    merged.filterBwHz = positiveOrNull(merged.filterBwHz)
    if (!FILTER_ORDERS.includes(merged.filterOrder as (typeof FILTER_ORDERS)[number])) {
      merged.filterOrder = defaults.filterOrder
    }
    merged.filterRejectionDb = Math.min(
      MAX_REJECTION_DB,
      Math.max(MIN_REJECTION_DB, positive(merged.filterRejectionDb, defaults.filterRejectionDb)),
    )
    if (merged.thresholdMode !== 'relative' && merged.thresholdMode !== 'absolute') merged.thresholdMode = 'relative'
    if (typeof merged.thresholdMarginDb !== 'number' || !Number.isFinite(merged.thresholdMarginDb)) {
      merged.thresholdMarginDb = defaults.thresholdMarginDb
    }
    if (typeof merged.thresholdAbsDb !== 'number' || !Number.isFinite(merged.thresholdAbsDb)) {
      merged.thresholdAbsDb = defaults.thresholdAbsDb
    }
    // A zero budget admits nothing at all, so the floor is one tenth of a dB.
    merged.fitLossBudgetDb = Math.min(MAX_REJECTION_DB, Math.max(0.1, positive(merged.fitLossBudgetDb, defaults.fitLossBudgetDb)))
    return merged
  } catch {
    return defaults
  }
}

function validTime(value: unknown): TimeInput | null {
  if (!value || typeof value !== 'object') return null
  const v = value as TimeInput
  if (v.kind === 'epoch' && typeof v.ms === 'number') return v
  if (v.kind === 'naive' && v.naive && typeof v.naive.year === 'number') return v
  return null
}

export function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(settings))
  } catch {
    // Private mode or blocked storage: settings simply do not persist.
  }
}
